import React from 'react';
import { Box, Typography, List, ListItem, Chip, Divider } from '@mui/material';
import { Clock, MapPin } from '@phosphor-icons/react';
import useThemeStyles from '../../../../theme/useThemeStyles';

const TimelineSection = ({ timeline }) => {
  const styles = useThemeStyles();

  const formatTime = (time) => {
    if (!time) return '';
    const hour = time.hour === 0 ? 12 : time.hour > 12 ? time.hour - 12 : time.hour;
    const minute = time.minute.toString().padStart(2, '0');
    return `${hour}:${minute} ${time.period}`;
  };

  const getCategoryIcon = (category) => {
    const icons = {
      ceremony: '💒',
      reception: '🎉',
      photo: '📸',
      setup: '🔧',
      other: '📋'
    };
    return icons[category] || '📋';
  };

  const getCategoryColor = (category) => {
    const colors = {
      ceremony: 'primary',
      reception: 'success',
      photo: 'info',
      setup: 'warning',
      other: 'default'
    };
    return colors[category] || 'default';
  };

  // Group timeline by day
  const timelineByDay = timeline?.reduce((acc, event) => {
    const day = event.day || 1;
    if (!acc[day]) {
      acc[day] = [];
    }
    acc[day].push(event);
    return acc;
  }, {}) || {};

  // Sort events within each day by time
  Object.keys(timelineByDay).forEach(day => {
    timelineByDay[day].sort((a, b) => {
      const timeA = a.time.hour * 60 + a.time.minute + (a.time.period === 'PM' && a.time.hour !== 12 ? 12 * 60 : 0);
      const timeB = b.time.hour * 60 + b.time.minute + (b.time.period === 'PM' && b.time.hour !== 12 ? 12 * 60 : 0);
      return timeA - timeB;
    });
  });

  if (!timeline || timeline.length === 0) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Clock size={24} style={{ marginRight: 12, color: '#64748b' }} />
          <Typography variant="h5" sx={{ fontWeight: 600, ...styles.text.primary }}>
            Timeline
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body2" sx={{ ...styles.text.secondary }}>
            No timeline events added yet
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Clock size={24} style={{ marginRight: 12, color: '#64748b' }} />
        <Typography variant="h5" sx={{ fontWeight: 600, ...styles.text.primary }}>
          Wedding Timeline
        </Typography>
      </Box>

      {Object.keys(timelineByDay).sort().map((day, _dayIndex) => (
        <Box key={day} sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, ...styles.text.primary }}>
            {parseInt(day) === 1 ? 'Wedding Day' : `Day ${day}`}
          </Typography>
          <List>
            {timelineByDay[day].map((event, index) => (
              <React.Fragment key={event._id || index}>
                <ListItem sx={{ py: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
                    <Box sx={{ mr: 2, minWidth: 80 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, ...styles.text.primary }}>
                        {formatTime(event.time)}
                      </Typography>
                      {event.duration && (
                        <Typography variant="caption" sx={{ ...styles.text.secondary }}>
                          {event.duration} min
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {event.title}
                        </Typography>
                        {event.category && (
                          <Chip
                            label={event.category}
                            size="small"
                            color={getCategoryColor(event.category)}
                            icon={<span>{getCategoryIcon(event.category)}</span>}
                          />
                        )}
                      </Box>
                      {event.description && (
                        <Typography variant="body2" sx={{ ...styles.text.secondary, mb: 1 }}>
                          {event.description}
                        </Typography>
                      )}
                      {event.location && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <MapPin size={14} />
                          <Typography variant="body2" sx={{ ...styles.text.secondary }}>
                            {event.location}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </ListItem>
                {index < timelineByDay[day].length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Box>
      ))}
    </Box>
  );
};

export default TimelineSection;

